import { Alert, Autocomplete, Box, Chip, Paper, Stack, TextField, Tooltip, Typography } from '@mui/material';
import type { GridColDef } from '@mui/x-data-grid';
import { DateTime } from 'luxon';
import { type FC, useMemo, useState } from 'react';

import type { MediaStatusSlot, MediaStatusStream } from '@okvc/schema';

import { formatBytes } from './format.js';
import StreamStateChip from './StreamStateChip.js';
import SummaryStat from './SummaryStat.js';
import { useMediaStatus } from '../../api/media-status.api.js';
import AutocompleteOptions from '../AutocompleteOptions.js';
import Table from '../Table/Table.js';

interface PatientOption {
  id: string;
  name: string;
}

const SlotTile: FC<{ slot: MediaStatusSlot }> = ({ slot }) => (
  <Paper variant="outlined" sx={{ p: 1.5, minWidth: 180 }}>
    <Stack direction="row" spacing={1} alignItems="center" justifyContent="space-between">
      <Typography variant="subtitle2">{slot.name}</Typography>
      <Chip
        size="small"
        label={slot.reachable ? 'Reachable' : 'Unreachable'}
        color={slot.reachable ? 'success' : 'error'}
      />
    </Stack>
    <Typography variant="caption" color="text.secondary">
      {slot.version ?? 'version unknown'}
      {' · '}
      {slot.pathCount !== null ? `${slot.pathCount} paths` : 'no data'}
    </Typography>
  </Paper>
);

const COLUMNS: GridColDef[] = [
  { field: 'patient', headerName: 'Patient', flex: 1 },
  { field: 'camera', headerName: 'Camera', flex: 1 },
  { field: 'quality', headerName: 'Quality', width: 90 },
  { field: 'slot', headerName: 'Server', width: 110 },
  {
    field: 'state',
    headerName: 'State',
    width: 130,
    renderCell: (params) => <StreamStateChip stream={params.row.stream as MediaStatusStream} />,
  },
  {
    field: 'onlineTime',
    headerName: 'Up since',
    width: 130,
    renderCell: (params) =>
      params.value ? DateTime.fromISO(params.value as string).toRelative() : '—',
  },
  { field: 'viewers', headerName: 'Viewers', type: 'number', width: 90 },
  {
    field: 'bytesPerMinute',
    headerName: 'Data/min',
    type: 'number',
    width: 110,
    renderCell: (params) => formatBytes(params.value as number | null),
  },
  {
    field: 'bytes24h',
    headerName: 'Data (24h)',
    type: 'number',
    width: 110,
    renderCell: (params) => formatBytes(params.value as number | null),
  },
  { field: 'drops24h', headerName: 'Drops (24h)', type: 'number', width: 110 },
  {
    field: 'lastError',
    headerName: 'Last error',
    flex: 1,
    sortable: false,
    renderCell: (params) => {
      const lastError = params.row.stream.lastError as MediaStatusStream['lastError'];
      if (!lastError) return '—';
      return (
        <Tooltip arrow title={`${lastError.message} (${DateTime.fromISO(lastError.at).toRelative()})`}>
          <Typography variant="body2" noWrap sx={{ color: 'error.main' }}>
            {lastError.message}
          </Typography>
        </Tooltip>
      );
    },
  },
];

const LiveStatusTab = () => {
  const { data, isLoading } = useMediaStatus();
  const [selectedPatient, setSelectedPatient] = useState<PatientOption | null>(null);
  const allStreams = useMemo(() => data?.streams ?? [], [data]);

  const patientOptions = useMemo(() => {
    const names = new Map<string, string>();
    for (const stream of allStreams) {
      if (stream.patientId && stream.patientName) {
        names.set(stream.patientId, stream.patientName);
      }
    }
    return [...names]
      .map(([id, name]) => ({ id, name }))
      .sort((a, b) => a.name.localeCompare(b.name));
  }, [allStreams]);

  const streams = useMemo(
    () => (selectedPatient ? allStreams.filter((s) => s.patientId === selectedPatient.id) : allStreams),
    [allStreams, selectedPatient],
  );

  const rows = useMemo(() => streams.map((stream) => ({
    id: stream.streamId,
    patient: stream.patientName ?? '—',
    camera: stream.cameraName ?? stream.cameraId,
    quality: stream.quality ?? '—',
    slot: stream.slot ?? '—',
    state: stream.state,
    onlineTime: stream.onlineTime,
    viewers: stream.viewers,
    bytesPerMinute: stream.bytesPerMinute,
    bytes24h: stream.bytes24h,
    drops24h: stream.drops24h,
    stream,
  })), [streams]);

  const totals = useMemo(() => {
    const expected = streams.filter((s) => s.expected);
    const hasIssue = (s: MediaStatusStream) =>
      s.state === 'missing' || s.state === 'error' || s.state === 'unknown' ||
      (s.state === 'pending' && s.pendingSince !== null);
    return {
      online: streams.filter((s) => s.state === 'online').length,
      expectedUp: `${expected.filter((s) => s.state === 'online').length}/${expected.length}`,
      viewers: streams.reduce((sum, s) => sum + s.viewers, 0),
      issues: streams.filter(hasIssue).length,
      bytesPerMinute: streams.reduce((sum, s) => sum + (s.bytesPerMinute ?? 0), 0),
    };
  }, [streams]);

  if (data && !data.available) {
    return <Alert severity="info">MediaMTX is not configured in this environment.</Alert>;
  }

  return (
    <>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Refreshes every 15 seconds; drop and data counters update every minute.
      </Typography>

      <Stack direction="row" spacing={2} flexWrap="wrap" useFlexGap sx={{ mb: 2 }}>
        {(data?.slots ?? []).map((slot) => <SlotTile key={slot.name} slot={slot} />)}
      </Stack>

      <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
        <Stack
          direction={{ xs: 'column', sm: 'row' }}
          spacing={2}
          alignItems={{ sm: 'center' }}
          justifyContent="space-between"
        >
          <Stack direction="row" spacing={4} flexWrap="wrap" useFlexGap>
            <SummaryStat label="Streams online" value={totals.online.toString()} emphasised />
            <SummaryStat label="Expected up" value={totals.expectedUp} />
            <SummaryStat label="Viewers" value={totals.viewers.toString()} />
            <SummaryStat label="Issues" value={totals.issues.toString()} />
            <SummaryStat label="Data/min" value={formatBytes(totals.bytesPerMinute)} />
          </Stack>
          <Box sx={{ width: { xs: '100%', sm: 280 } }}>
            <Autocomplete
              value={selectedPatient}
              onChange={(_, value: PatientOption | null) => setSelectedPatient(value)}
              id="media-status-patient-filter"
              options={patientOptions}
              isOptionEqualToValue={(option, value) => option.id === value.id}
              getOptionLabel={(option) => option.name}
              renderInput={(params) => <TextField {...params} label="Patient" size="small" />}
              renderOption={({ key, ...props }, option, { inputValue }) => (
                <AutocompleteOptions option={option.name} value={inputValue} key={key} {...props} />
              )}
            />
          </Box>
        </Stack>
      </Paper>

      <Table
        columns={COLUMNS}
        rows={rows}
        pageSize={25}
        isLoading={isLoading}
        NoRowsOverlayText="No MediaMTX streams right now."
        toolbar
      />
    </>
  );
};

export default LiveStatusTab;
